import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin, X, Loader2, Search, RefreshCw, Users, Clock,
  AlertTriangle, Bus, Flame, School, Accessibility, BellRing, ShieldAlert,
  Lightbulb, Sparkles, Pencil, Building2, Warehouse, Wrench, ShieldCheck,
  Plus, ListFilter, Maximize2, Minimize2, CheckCircle2, Layers, Move,
} from "lucide-react";
import POIEditorModal from "@/components/POIEditorModal";
import POIQualityDrawer from "@/components/POIQualityDrawer";

// ---- Type taxonomy (Phase B) -----------------------------------------------
// Drives icons, colours, filters and the legend.
const TYPE_META = {
  school:   { label: "École",    short: "Écoles",     glyph: "🏫", color: "#F59E0B" },
  stop:     { label: "Arrêt",    short: "Arrêts",     glyph: "🚏", color: "#3B82F6" },
  pmr:      { label: "PMR",      short: "PMR",        glyph: "♿", color: "#0EA5E9" },
  depot:    { label: "Dépôt",    short: "Dépôts",     glyph: "🏠", color: "#A16207" },
  workshop: { label: "Atelier",  short: "Ateliers",   glyph: "🔧", color: "#71717A" },
  client:   { label: "Client",   short: "Clients",    glyph: "🏢", color: "#10B981" },
  other:    { label: "Autre",    short: "Autres",     glyph: "📍", color: "#A1A1AA" },
};

// Status palette (kept stable across the page)
const STATUS_COLORS = {
  normal: { bg: "#22C55E", label: "À l'heure" },
  delay_light: { bg: "#FACC15", label: "Léger retard" },
  delay_important: { bg: "#F97316", label: "Retard important" },
  delay_critical: { bg: "#EF4444", label: "Retard critique" },
  no_service: { bg: "#A1A1AA", label: "Non desservi" },
};

const stopIcon = (stop, opts = {}) => {
  const { selected = false, edit = false } = opts;
  // Status drives the OUTLINE colour (delay severity), TYPE drives the FILL
  // and the centre glyph. So a quick glance still answers "Is this stop
  // running on time?" while also surfacing "Is this a school, depot, …?".
  const statusColor = STATUS_COLORS[stop.status]?.bg || "#A1A1AA";
  const typeMeta = TYPE_META[stop.type] || TYPE_META.other;
  const fill = typeMeta.color;
  const glyph = typeMeta.glyph;

  const ring = selected ? "0 0 0 3px #FFC60A, " : "";
  const pulse = (stop.status === "delay_critical" || stop.status === "delay_important")
    ? `<div style="position:absolute;inset:-8px;background:${statusColor};border-radius:999px;opacity:.35;animation:stpulse 1.6s ease-in-out infinite;"></div>` : "";
  // Visual cue when the POI is grab-able (edit-position mode).
  const editRing = edit
    ? `<div style="position:absolute;inset:-6px;border:2px dashed #EA580C;border-radius:999px;animation:stedit 1.4s ease-in-out infinite;pointer-events:none;"></div>` : "";
  const pmrBadge = stop.pmr_count > 0
    ? `<div style="position:absolute;top:-6px;right:-6px;background:#0A0A0B;color:#FFC60A;font-size:8px;font-weight:bold;padding:1px 3px;border-radius:6px;line-height:1;">♿</div>` : "";
  const incidentBadge = stop.incident_count > 0
    ? `<div style="position:absolute;top:-6px;left:-6px;background:#EF4444;color:#fff;font-size:8px;font-weight:bold;padding:1px 3px;border-radius:6px;line-height:1;">!</div>` : "";
  const inactiveStyle = stop.active === false ? "opacity:0.45;filter:grayscale(60%);" : "";
  const cursor = edit ? "cursor:grab;" : "";
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;${inactiveStyle}${cursor}">
      ${pulse}
      ${editRing}
      <div style="position:relative;width:28px;height:28px;background:${fill};border:3px solid ${edit ? "#EA580C" : statusColor};border-radius:999px;box-shadow:${ring}0 1px 3px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font-size:14px;line-height:1;">${glyph}</div>
      ${pmrBadge}
      ${incidentBadge}
    </div>
    <style>@keyframes stpulse { 0%,100% { transform: scale(1); opacity:.35; } 50% { transform: scale(1.8); opacity:0; } } @keyframes stedit { 0%,100% { opacity:.4; } 50% { opacity:1; } }</style>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points?.length) return;
    const b = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    if (b.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 14 });
  }, [map, points.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Imperative center-on-stop helper (driven by the left panel / quality drawer)
function FlyToStop({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lon], 16, { duration: 0.6 });
  }, [map, target]);
  return null;
}

function HeatLayer({ points, visible }) {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    if (!visible) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      return;
    }
    // Weight = combination of delay severity (1-4) + incident_count
    const heatPoints = points.map((p) => {
      const sev = p.status === "delay_critical" ? 1 :
                  p.status === "delay_important" ? 0.7 :
                  p.status === "delay_light" ? 0.4 : 0.1;
      const ic = Math.min(1, (p.incident_count || 0) * 0.3);
      return [p.lat, p.lon, Math.max(sev, ic)];
    });
    if (layerRef.current) map.removeLayer(layerRef.current);
    layerRef.current = L.heatLayer(heatPoints, { radius: 35, blur: 25, maxZoom: 16, max: 1.0 }).addTo(map);
    return () => { if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; } };
  }, [map, points, visible]);
  return null;
}

export default function StopsMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStop, setSelectedStop] = useState(null);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [lineFilter, setLineFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | school | pmr | incident
  const [sourceFilter, setSourceFilter] = useState("all"); // all | navixy | logibus | school | active | unused
  const [heatmapOn, setHeatmapOn] = useState(false);
  const [clustersOn, setClustersOn] = useState(true);
  const [editPositionMode, setEditPositionMode] = useState(false);
  const [editingStop, setEditingStop] = useState(null);
  const [showQuality, setShowQuality] = useState(false);
  const [showList, setShowList] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [usage, setUsage] = useState({});
  const [flyTo, setFlyTo] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, usageRes] = await Promise.all([
        api.get("/logibus/stops-map"),
        api.get("/logibus/poi/usage").catch(() => ({ data: { usage: {} } })),
      ]);
      setData(data);
      setUsage(usageRes.data?.usage || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    pollRef.current = setInterval(load, 15000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const loadDetail = async (stopId) => {
    setDetail(null);
    setHistory(null);
    try {
      const [{ data: d }, { data: h }] = await Promise.all([
        api.get(`/logibus/stops-map/${stopId}`),
        api.get(`/logibus/stops-map/${stopId}/history?days=7`).catch(() => ({ data: null })),
      ]);
      setDetail(d);
      setHistory(h);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  /** Persist a drag-and-drop move of a POI. Triggered by Leaflet's
   *  ``dragend`` event on a Marker when editPositionMode is ON.
   *  Optimistically updates the local list so the marker stays at its new
   *  spot even if the next poll arrives before the PATCH response. */
  const updateStopPosition = async (stopId, latlng) => {
    const lat = Number(latlng.lat.toFixed(6));
    const lon = Number(latlng.lng.toFixed(6));
    // optimistic local update
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stops: (prev.stops || []).map((s) =>
          s.stop_id === stopId ? { ...s, lat, lon } : s,
        ),
      };
    });
    try {
      await api.patch(`/logibus/transit/stops/${stopId}`, {
        stop_lat: lat, stop_lon: lon,
      });
      toast.success(`POI déplacé · ${lat.toFixed(5)}, ${lon.toFixed(5)}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      load();  // revert by reloading the truth
    }
  };

  const allRoutes = data?.routes || [];
  const allStops = data?.stops || [];
  const kpis = data?.kpis || {};

  const filteredStops = useMemo(() => {
    const q = search.toLowerCase();
    return allStops.filter((s) => {
      if (q && !(s.stop_name + " " + (s.stop_id || "") + " " + (s.city || "")).toLowerCase().includes(q)) return false;
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (lineFilter && !s.lines.some((l) => l.route_id === lineFilter)) return false;
      // Type filter now relies on the explicit ``type`` field set by the
      // classifier / mini-editor. Falls back to "stop" when missing so
      // legacy GTFS rows still render under "Arrêt".
      if (typeFilter !== "all" && (s.type || "stop") !== typeFilter) return false;
      // Source-based filters
      const src = (s.source || "GTFS").toLowerCase();
      if (sourceFilter === "navixy" && src !== "navixy") return false;
      if (sourceFilter === "logibus" && !(src === "logibus" || src === "gtfs")) return false;
      if (sourceFilter === "school" && (s.type || "stop") !== "school") return false;
      if (sourceFilter === "active" && (usage[s.stop_id] ?? 0) === 0) return false;
      if (sourceFilter === "unused" && (usage[s.stop_id] ?? 0) > 0) return false;
      if (sourceFilter === "inactive" && s.active !== false) return false;
      return true;
    });
  }, [allStops, search, statusFilter, lineFilter, typeFilter, sourceFilter, usage]);

  const sourceCounts = useMemo(() => {
    const c = { navixy: 0, logibus: 0, school: 0, active: 0, unused: 0, inactive: 0, all: allStops.length };
    allStops.forEach((s) => {
      const src = (s.source || "GTFS").toLowerCase();
      if (src === "navixy") c.navixy += 1;
      else c.logibus += 1;
      if ((s.type || "stop") === "school") c.school += 1;
      if ((usage[s.stop_id] ?? 0) > 0) c.active += 1; else c.unused += 1;
      if (s.active === false) c.inactive += 1;
    });
    return c;
  }, [allStops, usage]);

  const typeCounts = useMemo(() => {
    const c = {};
    Object.keys(TYPE_META).forEach((k) => { c[k] = 0; });
    allStops.forEach((s) => { c[s.type || "stop"] = (c[s.type || "stop"] || 0) + 1; });
    return c;
  }, [allStops]);

  const stats = useMemo(() => {
    const out = { total: allStops.length, normal: 0, delay_light: 0, delay_important: 0, delay_critical: 0, no_service: 0 };
    allStops.forEach((s) => { out[s.status] = (out[s.status] || 0) + 1; });
    return out;
  }, [allStops]);

  const notify = async (audience, severity = "info") => {
    if (!detail) return;
    try {
      await api.post(`/logibus/stops-map/${detail.stop_id}/notify`, {
        audience, severity,
        message: detail.recommendation?.body || null,
      });
      toast.success(`Notification ${audience} envoyée (file d'attente — Resend/SMS désactivés)`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  if (loading && !data) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm font-mono text-zinc-500">
        <Loader2 className="animate-spin" size={14} /> Chargement de la tour de contrôle…
      </div>
    );
  }

  // Compact KPI bar (4 cards — orientation exploitation, pas reporting)
  const qualityIssues =
    (sourceCounts.unused || 0) + (sourceCounts.inactive || 0); // proxy léger ; le drawer affiche le détail complet

  return (
    <div className={`${fullscreen ? "fixed inset-0 z-[800] bg-white p-3" : "p-4 lg:p-6"}`} data-testid="stops-map-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Arrêts · Gestion cartographique des POI</div>
          <h1 className="font-display font-black uppercase text-2xl lg:text-3xl tracking-tighter">Carte du réseau</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setFullscreen((v) => !v)} data-testid="toggle-fullscreen"
            className={`inline-flex items-center gap-1 px-3 py-2 border border-zinc-900 text-xs font-bold uppercase tracking-widest ${fullscreen ? "bg-zinc-900 text-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
            {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />} {fullscreen ? "Réduire" : "Plein écran"}
          </button>
          <button onClick={() => setShowList((v) => !v)} data-testid="toggle-poi-list"
            className={`inline-flex items-center gap-1 px-3 py-2 border border-zinc-900 text-xs font-bold uppercase tracking-widest ${showList ? "bg-zinc-900 text-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
            <ListFilter size={13} /> Liste POI
          </button>
          <button onClick={() => setShowQuality(true)} data-testid="open-quality-drawer"
            className="inline-flex items-center gap-1 px-3 py-2 border border-zinc-900 bg-white hover:bg-amber-50 text-xs font-bold uppercase tracking-widest">
            <ShieldCheck size={13} /> Qualité POI
          </button>
          <button onClick={() => setEditPositionMode((v) => !v)} data-testid="edit-position-toggle"
            title={editPositionMode ? "Désactiver le déplacement" : "Glisser-déposer les POI pour corriger leur position (utile pour les arrêts mal géolocalisés, par ex. dans le lac)"}
            className={`inline-flex items-center gap-1 px-3 py-2 border border-zinc-900 text-xs font-bold uppercase tracking-widest ${editPositionMode ? "bg-orange-500 text-white border-orange-700" : "bg-white hover:bg-orange-50"}`}>
            <Move size={13} /> {editPositionMode ? "Position : ON" : "Corriger position"}
          </button>
          <button onClick={() => setClustersOn((v) => !v)} data-testid="clusters-toggle"
            title={clustersOn ? "Désactiver le regroupement pour voir tous les POI à la fois" : "Réactiver le regroupement"}
            className={`inline-flex items-center gap-1 px-3 py-2 border border-zinc-900 text-xs font-bold uppercase tracking-widest ${clustersOn ? "bg-white hover:bg-zinc-100" : "bg-zinc-900 text-[#FFC60A]"}`}>
            <Layers size={13} /> {clustersOn ? "Étaler les POI" : "Regrouper"}
          </button>
          <button onClick={() => setHeatmapOn((v) => !v)} data-testid="heatmap-toggle"
            className={`inline-flex items-center gap-1 px-3 py-2 border border-zinc-900 text-xs font-bold uppercase tracking-widest ${heatmapOn ? "bg-zinc-900 text-[#FFC60A]" : "bg-white hover:bg-zinc-100"}`}>
            <Flame size={13} /> Heatmap {heatmapOn ? "ON" : "OFF"}
          </button>
          <button onClick={load} data-testid="refresh-stops-map"
            className="inline-flex items-center gap-2 bg-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#ffd43a]">
            <RefreshCw size={14} /> Rafraîchir
          </button>
        </div>
      </header>

      {/* Compact KPI bar — 4 indicateurs orientés exploitation */}
      {!fullscreen && (
        <div className="grid grid-cols-2 lg:grid-cols-4 border border-zinc-900 mb-3" data-testid="poi-kpis">
          <CompactKpi
            label="Arrêts actifs" value={stats.total} icon={<MapPin size={11} />}
            active={sourceFilter === "all"}
            onClick={() => setSourceFilter("all")} testId="kpi-active"
          />
          <CompactKpi
            label="Écoles" value={typeCounts.school || 0} icon={<School size={11} />} accent="#F59E0B"
            active={typeFilter === "school"}
            onClick={() => setTypeFilter((p) => p === "school" ? "all" : "school")} testId="kpi-schools"
          />
          <CompactKpi
            label="Utilisés" value={sourceCounts.active || 0} icon={<CheckCircle2 size={11} />} accent="#10B981"
            active={sourceFilter === "active"}
            onClick={() => setSourceFilter((p) => p === "active" ? "all" : "active")} testId="kpi-used"
          />
          <CompactKpi
            label="Problèmes" value={qualityIssues} icon={<ShieldAlert size={11} />} accent={qualityIssues ? "#EF4444" : "#A1A1AA"}
            onClick={() => setShowQuality(true)} testId="kpi-quality"
          />
        </div>
      )}

      {/* Edit-position mode hint */}
      {editPositionMode && (
        <div data-testid="edit-position-banner"
              className="border-2 border-orange-500 bg-orange-50 px-3 py-2 mb-3 flex items-center gap-3 shadow-[3px_3px_0_0_#EA580C]">
          <Move size={16} className="text-orange-700 shrink-0" />
          <div className="flex-1 text-sm">
            <strong>Mode correction de position activé.</strong> Glissez n&apos;importe quel POI vers sa vraie position
            (utile pour les arrêts placés dans l&apos;eau ou à côté). Les modifications sont enregistrées dès le relâchement.
          </div>
          <button
            onClick={() => setEditPositionMode(false)}
            data-testid="edit-position-banner-done"
            className="px-3 py-1 bg-orange-500 text-white border border-orange-700 text-[10px] font-black uppercase tracking-widest hover:bg-orange-600">
            Terminer
          </button>
        </div>
      )}

      <div className={`grid gap-0 border border-zinc-900 ${fullscreen ? "min-h-[88vh]" : "min-h-[76vh]"} lg:grid-cols-12`}>
        {/* Left: POI list (Phase B) — slim 2 cols, hideable */}
        {showList && (
          <aside className={`lg:col-span-2 border-r border-zinc-900 bg-white ${fullscreen ? "max-h-[88vh]" : "max-h-[76vh]"} overflow-y-auto`} data-testid="poi-list-panel">
            <div className="px-2 py-2 border-b border-zinc-900 bg-zinc-50 sticky top-0 z-10">
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">POI · Liste</div>
              <div className="font-display font-black text-sm tracking-tighter mt-0.5">{filteredStops.length} affiché{filteredStops.length > 1 ? "s" : ""}</div>
              {/* In-panel search (always visible) */}
              <div className="mt-2 flex items-center gap-1 border border-zinc-900 bg-white px-2 py-1">
                <Search size={10} className="text-zinc-500 flex-shrink-0" />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher…" data-testid="poi-list-search"
                  className="flex-1 min-w-0 outline-none text-[11px] font-mono" />
                {search && <button onClick={() => setSearch("")}><X size={10} /></button>}
              </div>
              {/* Type quick-filter chips */}
              {Object.entries(typeCounts).filter(([, v]) => v > 0).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {Object.entries(TYPE_META).map(([k, m]) => (
                    typeCounts[k] > 0 && (
                      <button key={k}
                        onClick={() => setTypeFilter((p) => p === k ? "all" : k)}
                        title={`${m.label} (${typeCounts[k]})`}
                        data-testid={`type-quickfilter-${k}`}
                        className={`w-6 h-6 border flex items-center justify-center text-[11px] ${typeFilter === k ? "border-zinc-900 ring-2 ring-[#FFC60A]" : "border-zinc-300 hover:border-zinc-900"}`}
                        style={{ background: m.color, color: "white" }}
                      >{m.glyph}</button>
                    )
                  ))}
                  {typeFilter !== "all" && (
                    <button onClick={() => setTypeFilter("all")} title="Réinitialiser"
                      className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 underline self-center">
                      reset
                    </button>
                  )}
                </div>
              )}
            </div>
            {filteredStops.length === 0 ? (
              <div className="p-8 text-center text-xs text-zinc-400">
                <MapPin size={28} className="mx-auto text-zinc-300 mb-2" />
                Aucun POI ne correspond aux filtres actifs.
              </div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {filteredStops.slice(0, 200).map((s) => {
                  const meta = TYPE_META[s.type || "stop"] || TYPE_META.other;
                  const used = usage[s.stop_id] ?? 0;
                  const isSel = selectedStop === s.stop_id;
                  return (
                    <li key={s.stop_id}
                      data-testid={`poi-row-${s.stop_id}`}
                      onClick={() => { setSelectedStop(s.stop_id); loadDetail(s.stop_id); setFlyTo({ lat: s.lat, lon: s.lon, stop_id: s.stop_id }); }}
                      className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-[#FFF7D6] ${isSel ? "bg-[#FFF7D6]" : ""} ${s.active === false ? "opacity-50" : ""}`}>
                      <span
                        className="w-7 h-7 flex items-center justify-center border border-zinc-900 flex-shrink-0 text-sm"
                        style={{ background: meta.color, color: "white" }}
                      >{meta.glyph}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate">{s.stop_name}</div>
                        <div className="text-[10px] font-mono text-zinc-500 truncate">
                          {s.city || s.stop_id}
                          {used > 0 && <span className="ml-1 text-emerald-700 font-bold">· {used} tournée{used > 1 ? "s" : ""}</span>}
                          {s.active === false && <span className="ml-1 text-red-700 font-bold">· INACTIF</span>}
                        </div>
                      </div>
                      <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border border-zinc-300 bg-white flex-shrink-0">
                        {meta.short}
                      </span>
                    </li>
                  );
                })}
                {filteredStops.length > 200 && (
                  <li className="p-3 text-center text-[10px] text-zinc-500 font-mono">
                    + {filteredStops.length - 200} autres POIs… affinez les filtres
                  </li>
                )}
              </ul>
            )}
          </aside>
        )}

        {/* Map — element principal de la page */}
        <section
          className={`${showList ? (detail ? "lg:col-span-7" : "lg:col-span-10") : (detail ? "lg:col-span-9" : "lg:col-span-12")} ${detail ? "border-r border-zinc-900" : ""} relative`}
          data-testid="stops-map"
        >
          <div className="absolute top-2 right-2 z-[500] flex gap-1 flex-wrap items-center bg-white border border-zinc-900 p-1">
            <select value={lineFilter} onChange={(e) => setLineFilter(e.target.value)} data-testid="line-filter"
              className="text-xs font-mono p-1 outline-none bg-white">
              <option value="">Toutes les lignes</option>
              {allRoutes.map((r) => <option key={r.route_id} value={r.route_id}>{`Ligne ${r.route_short_name}`}</option>)}
            </select>
          </div>
          <MapContainer
            center={[46.51, 6.49]}
            zoom={12}
            scrollWheelZoom={true}
            attributionControl={false}
            style={{ height: fullscreen ? "88vh" : "76vh", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
            <FitBounds points={filteredStops} />
            <FlyToStop target={flyTo} />
            <HeatLayer points={filteredStops} visible={heatmapOn} />
            {(() => {
              // Drag mode reuses the same marker block in both clustered and
              // flat modes. We only need the user to be able to grab + drop
              // an icon: even inside a cluster, dragging an opened (spiderfy)
              // marker works fine in leaflet.markercluster.
              const renderMarker = (s) => {
                const meta = TYPE_META[s.type || "stop"] || TYPE_META.other;
                const used = usage[s.stop_id] ?? 0;
                return (
                  <Marker
                    key={s.stop_id}
                    position={[s.lat, s.lon]}
                    icon={stopIcon(s, { selected: selectedStop === s.stop_id, edit: editPositionMode })}
                    draggable={editPositionMode}
                    eventHandlers={{
                      click: () => {
                        if (editPositionMode) return;  // don't open detail while dragging
                        setSelectedStop(s.stop_id); loadDetail(s.stop_id);
                      },
                      dragend: (ev) => {
                        const ll = ev.target.getLatLng();
                        updateStopPosition(s.stop_id, ll);
                      },
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -16]} opacity={0.95}>
                      <strong>{s.stop_name}</strong>
                      <div className="text-[10px] mt-0.5">
                        <span style={{ color: meta.color }}>{meta.glyph} {meta.label}</span>
                        {s.city && <> · {s.city}</>}
                        {s.active === false && <span style={{ color: "#b91c1c" }}> · INACTIF</span>}
                      </div>
                      {editPositionMode && (
                        <div className="text-[10px] mt-0.5 font-bold" style={{ color: "#EA580C" }}>
                          ✋ Glissez pour déplacer
                        </div>
                      )}
                      {used > 0 && (
                        <div className="text-[10px] mt-0.5" style={{ color: "#047857" }}>
                          Utilisé dans {used} tournée{used > 1 ? "s" : ""}
                        </div>
                      )}
                      {s.lines.length > 0 && <div className="text-[10px]">Lignes : {s.lines.map((l) => l.route_short_name).join(", ")}</div>}
                      {(s.passenger_count > 0 || s.pmr_count > 0 || s.incident_count > 0) && (
                        <div className="text-[10px] mt-0.5">
                          {s.passenger_count > 0 && <>👦 {s.passenger_count} </>}
                          {s.pmr_count > 0 && <>♿ {s.pmr_count} </>}
                          {s.incident_count > 0 && <>⚠️ {s.incident_count}</>}
                        </div>
                      )}
                    </Tooltip>
                  </Marker>
                );
              };
              // While edit-position mode is ON we always bypass clustering so
              // every marker is individually grab-able. Otherwise honour the
              // user's cluster preference.
              const forceFlat = editPositionMode;
              if (clustersOn && !forceFlat) {
                return (
                  <MarkerClusterGroup
                    chunkedLoading
                    disableClusteringAtZoom={12}
                    maxClusterRadius={35}
                    spiderfyOnMaxZoom
                    showCoverageOnHover
                    zoomToBoundsOnClick
                    spiderfyDistanceMultiplier={1.6}>
                    {filteredStops.map(renderMarker)}
                  </MarkerClusterGroup>
                );
              }
              return filteredStops.map(renderMarker);
            })()}
          </MapContainer>
        </section>

        {/* Side panel — collapsed by default; only shown when a stop is selected */}
        {selectedStop && (
          <aside className={`lg:col-span-3 ${fullscreen ? "max-h-[88vh]" : "max-h-[76vh]"} overflow-y-auto bg-white`} data-testid="stop-side-panel">
            {!detail && (
              <div className="p-6 flex items-center gap-2 text-sm font-mono text-zinc-500">
                <Loader2 className="animate-spin" size={14} /> Chargement…
              </div>
            )}
            {detail && <StopDetailPanel detail={detail} history={history} stops={allStops} onClose={() => { setSelectedStop(null); setDetail(null); }} onNotify={notify} onEdit={() => setEditingStop(allStops.find((s) => s.stop_id === detail.stop_id) || detail)} />}
          </aside>
        )}
      </div>
      {editingStop && (
        <POIEditorModal
          stop={{
            stop_id: editingStop.stop_id,
            stop_name: editingStop.stop_name,
            address: editingStop.address,
            city: editingStop.city,
            postal_code: editingStop.postal_code,
            type: editingStop.type,
            active: editingStop.active,
            notes: editingStop.notes,
            source: editingStop.source,
            external_id: editingStop.external_id,
          }}
          onClose={() => setEditingStop(null)}
          onSaved={(fresh) => {
            setEditingStop(null);
            load();
            if (selectedStop === fresh?.stop_id) loadDetail(fresh.stop_id);
            toast.success(`POI « ${fresh?.stop_name || ""} » mis à jour`);
          }}
        />
      )}
      {showQuality && (
        <POIQualityDrawer
          onClose={() => setShowQuality(false)}
          onEditStop={(s) => { setShowQuality(false); setEditingStop(s); }}
          onFocusStop={(stop_id) => {
            const s = allStops.find((x) => x.stop_id === stop_id);
            if (s) {
              setShowQuality(false);
              setSelectedStop(stop_id);
              loadDetail(stop_id);
              setFlyTo({ lat: s.lat, lon: s.lon, stop_id });
            }
          }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function CompactKpi({ label, value, icon, accent, active = false, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`p-2.5 border-r border-zinc-900 last:border-r-0 text-left transition-colors ${
        active ? "bg-[#FFF7D6]" : "bg-white hover:bg-zinc-50"
      }`}
    >
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center justify-center w-4 h-4" style={{ color: accent || "#0A0A0B" }}>{icon}</span>
        <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{label}</span>
      </div>
      <div className="mt-1 font-display font-black text-2xl tracking-tighter" style={accent ? { color: accent } : undefined}>{value}</div>
    </button>
  );
}

function SupKpi({ label, value, icon, tone = "default", highlight = false }) {  const tones = {
    default: "bg-white",
    green: "bg-emerald-50",
    orange: "bg-orange-50",
    red: "bg-red-50",
    blue: "bg-blue-50",
  };
  return (
    <div className={`p-3 border-r border-zinc-900 last:border-r-0 ${highlight ? "bg-[#FFC60A]" : tones[tone]}`}>
      <div className="flex items-center gap-1.5">
        <span className="text-zinc-700">{icon}</span>
        <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-700">{label}</span>
      </div>
      <div className="mt-1 font-display font-black text-2xl tracking-tighter">{value}</div>
    </div>
  );
}

function StopDetailPanel({ detail, history, stops, onClose, onNotify, onEdit }) {
  const stop = stops.find((s) => s.stop_id === detail.stop_id);
  const status = STATUS_COLORS[stop?.status || "no_service"];
  const reco = detail.recommendation;
  const recoTone = reco?.level === "critical" ? "border-red-600 bg-red-50" :
                   reco?.level === "warning" ? "border-orange-600 bg-orange-50" :
                                                "border-zinc-900 bg-[#FFF7D6]";
  return (
    <>
      <div className="p-4 border-b border-zinc-900 bg-zinc-900 text-white" data-testid="stop-detail-header">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-400">Tour de contrôle · Arrêt</div>
            <div className="font-display font-black text-xl tracking-tighter mt-0.5 truncate">{detail.stop_name}</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-xs">
                <span className="w-2 h-2 rounded-full" style={{ background: status.bg }} />
                {status.label}
              </span>
              {stop?.type && (
                <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-zinc-500 text-zinc-300">
                  {stop.type}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {onEdit && (
              <button
                onClick={onEdit}
                data-testid="edit-stop-btn"
                title="Modifier ce POI (type, nom, adresse, statut)"
                className="px-2 py-1 border border-zinc-500 text-zinc-200 hover:bg-[#FFC60A] hover:text-zinc-900 hover:border-zinc-900 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest"
              >
                <Pencil size={11} /> Modifier
              </button>
            )}
            <button onClick={onClose} data-testid="close-stop-panel" className="text-zinc-400 hover:text-white p-1">
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Mini stats */}
      <div className="p-3 grid grid-cols-3 gap-2 border-b border-zinc-200" data-testid="stop-stats">
        <MiniStat icon={<Users size={12} />} label="Passagers" value={detail.children_attendus?.length || 0} />
        <MiniStat icon={<Accessibility size={12} />} label="PMR" value={detail.pmr_count || 0} tone={detail.pmr_count > 0 ? "blue" : "default"} />
        <MiniStat icon={<ShieldAlert size={12} />} label="Incidents" value={detail.incidents?.length || 0} tone={detail.incidents?.length ? "red" : "default"} />
      </div>

      {/* Lines */}
      <div className="p-3 border-b border-zinc-200">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Lignes desservantes</div>
        <div className="flex flex-wrap gap-1">
          {(stop?.lines || []).length === 0 && <span className="text-xs text-zinc-400">—</span>}
          {(stop?.lines || []).map((l) => (
            <span key={l.route_id} className="text-xs font-display font-bold border border-zinc-900 px-1.5 py-0.5"
              style={{ background: `#${l.color || "FFC60A"}`, color: l.color === "0A0A0B" ? "#FFC60A" : "#0A0A0B" }}>
              {l.route_short_name}
            </span>
          ))}
        </div>
      </div>

      {/* AI recommendation */}
      {reco && (
        <div className={`m-3 border ${recoTone} p-3`} data-testid="ai-reco">
          <div className="flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase">
            <Lightbulb size={11} /> Recommandation IA · {reco.level === "critical" ? "Critique" : reco.level === "warning" ? "Attention" : "Info"}
          </div>
          <div className="font-bold text-sm mt-1">{reco.title}</div>
          <div className="text-xs text-zinc-700 mt-0.5">{reco.body}</div>
        </div>
      )}

      {/* Action buttons */}
      <div className="px-3 pb-3 grid grid-cols-3 gap-1" data-testid="stop-actions">
        <button onClick={() => onNotify("parents", "info")} data-testid="notify-parents"
          className="border border-zinc-900 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white inline-flex items-center justify-center gap-1">
          <BellRing size={10} /> Parents
        </button>
        <button onClick={() => onNotify("driver", "info")} data-testid="notify-driver"
          className="border border-zinc-900 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white inline-flex items-center justify-center gap-1">
          <BellRing size={10} /> Chauffeur
        </button>
        <button onClick={() => onNotify("operator", "warning")} data-testid="notify-operator"
          className="border border-zinc-900 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white inline-flex items-center justify-center gap-1">
          <BellRing size={10} /> Exploitant
        </button>
      </div>

      {/* Open incidents */}
      {detail.incidents?.length > 0 && (
        <div className="p-3 border-t border-zinc-200" data-testid="stop-incidents">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 inline-flex items-center gap-1">
            <AlertTriangle size={11} /> Incidents ouverts ({detail.incidents.length})
          </div>
          <div className="space-y-1">
            {detail.incidents.slice(0, 3).map((i) => (
              <div key={i.id} className="border border-orange-300 bg-orange-50 p-2 text-xs">
                <div className="font-bold uppercase">{i.type}</div>
                <div className="text-zinc-700">{i.message}</div>
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{(i.created_at || "").slice(0, 16).replace("T", " ")} · {i.reporter_name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Children expected */}
      {detail.children_attendus?.length > 0 && (
        <div className="p-3 border-t border-zinc-200" data-testid="stop-children">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 inline-flex items-center gap-1">
            <School size={11} /> Passagers attendus ({detail.children_attendus.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {detail.children_attendus.slice(0, 12).map((c) => (
              <span key={c.id} className="text-[10px] font-mono px-1.5 py-0.5 border border-zinc-300 bg-zinc-50">
                {c.is_pmr && <span className="text-blue-700">♿ </span>}
                {c.first_name} {(c.last_name || "").slice(0, 1).toUpperCase()}{c.grade ? ` · ${c.grade}` : ""}
              </span>
            ))}
            {detail.children_attendus.length > 12 && (
              <span className="text-[10px] font-mono text-zinc-500">+{detail.children_attendus.length - 12}…</span>
            )}
          </div>
        </div>
      )}

      {/* History mini-chart (7d) */}
      {history && history.buckets?.length > 0 && (
        <div className="p-3 border-t border-zinc-200" data-testid="stop-history">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            Historique 7 jours · {history.total_events} retard{history.total_events > 1 ? "s" : ""} · {history.total_incidents} incident{history.total_incidents > 1 ? "s" : ""}
          </div>
          <HistoryBars buckets={history.buckets} />
        </div>
      )}

      {/* Passages */}
      <div className="p-3 border-t border-zinc-200" data-testid="stop-passages">
        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2 inline-flex items-center gap-1">
          <Clock size={11} /> Prochains passages
        </div>
        <div className="space-y-1">
          {(detail.passages || []).length === 0 && <div className="text-xs text-zinc-400">Aucun passage prévu.</div>}
          {(detail.passages || []).slice(0, 10).map((p, i) => (
            <div key={p.trip_id + "_" + i} className={`border ${p.delay_minutes > 0 ? "border-orange-400 bg-orange-50" : "border-zinc-200"} p-2 text-xs`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-display font-black text-xs border border-zinc-900 px-1.5 py-0.5 shrink-0">{p.route_short_name}</span>
                  <span className="font-semibold truncate">→ {p.headsign}</span>
                </div>
                {p.live && <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-green-700"><Bus size={9} /> Live</span>}
              </div>
              <div className="mt-1 flex items-center gap-3 font-mono">
                <span className="text-zinc-600">Prévu : {p.scheduled?.slice(0, 5)}</span>
                <span className={p.delay_minutes > 0 ? "text-orange-700 font-bold" : "text-zinc-600"}>Estimé : {p.estimated}</span>
                {p.delay_minutes > 0 && <span className="text-orange-700 font-bold">+{p.delay_minutes} min</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function MiniStat({ icon, label, value, tone = "default" }) {
  const tones = {
    default: "bg-white",
    blue: "bg-blue-50 border-blue-300",
    red: "bg-red-50 border-red-300",
  };
  return (
    <div className={`border ${tones[tone] || "border-zinc-900"} p-2`}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="mt-0.5 font-display font-black text-lg tracking-tighter">{value}</div>
    </div>
  );
}

function HistoryBars({ buckets }) {
  const maxIncident = Math.max(1, ...buckets.map((b) => b.incident_count));
  const maxDelay = Math.max(1, ...buckets.map((b) => b.delay_count));
  return (
    <div className="flex items-end gap-1 h-20" data-testid="history-bars">
      {buckets.map((b) => {
        const dh = (b.delay_count / maxDelay) * 100;
        const ih = (b.incident_count / maxIncident) * 100;
        const label = b.day.slice(5);
        return (
          <div key={b.day} className="flex-1 flex flex-col items-center" title={`${b.day} · ${b.delay_count} retards · ${b.incident_count} incidents · avg ${b.avg_delay_min} min`}>
            <div className="w-full flex items-end gap-0.5 h-16">
              <div className="flex-1 bg-orange-400" style={{ height: `${dh}%` }} />
              <div className="flex-1 bg-red-500" style={{ height: `${ih}%` }} />
            </div>
            <div className="text-[8px] font-mono text-zinc-500 mt-0.5">{label}</div>
          </div>
        );
      })}
    </div>
  );
}
